import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import type {
  MarketCategory,
  MarketListingSummary,
  MarketPerson,
  MarketProvider,
  MarketReview,
} from '@/types/market';
import { DEFAULT_AVATAR, ListingCard, Stars } from './MarketShared';
import styles from './Market.module.scss';

export default function MarketProviders() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const personId = searchParams.get('person');

  return personId
    ? <ProviderProfile key={personId} personId={personId} navigate={navigate} />
    : <ProviderDirectory navigate={navigate} searchParams={searchParams} setSearchParams={setSearchParams} />;
}

/* ---------------- Directory ---------------- */
function ProviderDirectory({
  navigate, searchParams, setSearchParams,
}: {
  navigate: ReturnType<typeof useNavigate>;
  searchParams: URLSearchParams;
  setSearchParams: (p: URLSearchParams) => void;
}) {
  const category = searchParams.get('category') || '';
  const city = searchParams.get('city') || '';
  const qParam = searchParams.get('q') || '';

  const [q, setQ] = useState(qParam);
  const [categories, setCategories] = useState<MarketCategory[]>([]);
  const [providers, setProviders] = useState<MarketProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => { setQ(qParam); }, [qParam]);

  useEffect(() => {
    mounted.current = true;
    (async () => {
      try {
        const cats = await apiService.mktGetCategories('service');
        if (mounted.current) setCategories(cats);
      } catch { /* non-fatal */ }
    })();
    return () => { mounted.current = false; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await apiService.mktGetProviders({
        category: category || undefined,
        city: city || undefined,
        q: qParam || undefined,
      });
      if (!mounted.current) return;
      setProviders(list);
      setError(null);
    } catch (e: any) {
      if (mounted.current) setError(e?.message || 'Failed to load providers.');
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [category, city, qParam]);

  useEffect(() => { load(); }, [load]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next);
  };

  const catName = (code: string) => categories.find((c) => c.code === code)?.name || code;

  return (
    <>
      <DashboardHeader />
      <div className={styles.container}>
        <div className={styles.topBar}>
          <button type="button" className={styles.backButton} onClick={() => navigate('/customer/market')}>← Marketplace</button>
        </div>
        <h1 className={styles.title}>Service directory</h1>
        <p className={styles.subtitle}>Find trusted service providers by category and city.</p>

        <form className={styles.searchRow} onSubmit={(e) => { e.preventDefault(); updateParam('q', q.trim()); }}>
          <input className={styles.searchInput} placeholder="Search providers…" value={q} onChange={(e) => setQ(e.target.value)} />
          <button type="submit" className={styles.secondaryBtn}>Search</button>
        </form>

        <div className={styles.filters}>
          <select className={styles.select} value={category} onChange={(e) => updateParam('category', e.target.value)}>
            <option value="">All services</option>
            {categories.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
          <input className={styles.select} placeholder="City" defaultValue={city}
            onBlur={(e) => updateParam('city', e.target.value.trim())}
            onKeyDown={(e) => { if (e.key === 'Enter') updateParam('city', (e.target as HTMLInputElement).value.trim()); }} />
        </div>

        {error && <p className={styles.error}>{error}</p>}
        {loading && <p className={styles.muted}><span className={styles.spinner} /> Loading…</p>}

        {!loading && providers.length === 0 && !error && (
          <div className={styles.empty}><div className={styles.emptyIcon}>🧰</div>No providers found.</div>
        )}

        {!loading && providers.length > 0 && (
          <div className={styles.providerList}>
            {providers.map((p) => (
              <div key={p.person.id} className={styles.providerRow}
                onClick={() => navigate(`/customer/market/providers?person=${encodeURIComponent(p.person.id)}`)}
                role="button" tabIndex={0}>
                <img className={styles.avatar} src={p.person.profilePic || DEFAULT_AVATAR} alt={p.person.displayName}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR; }} />
                <div className={styles.providerInfo}>
                  <div className={styles.personName}>{p.person.displayName}</div>
                  <div className={styles.personSub}>
                    {p.person.city || '—'} · {p.serviceCount} service{p.serviceCount !== 1 ? 's' : ''}
                  </div>
                  <Stars value={p.avgRating} count={p.reviewCount} />
                  {p.sampleCategories?.length > 0 && (
                    <div className={styles.chips}>
                      {p.sampleCategories.map((code) => <span key={code} className={styles.chip}>{catName(code)}</span>)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}

/* ---------------- Provider public profile ---------------- */
function ProviderProfile({
  personId, navigate,
}: {
  personId: string;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [person, setPerson] = useState<MarketPerson | null>(null);
  const [listings, setListings] = useState<MarketListingSummary[]>([]);
  const [reviews, setReviews] = useState<MarketReview[]>([]);
  const [reviewAvg, setReviewAvg] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Trust & safety
  const [isSelf, setIsSelf] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const mounted = useRef(true);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => { if (mounted.current) setToast(null); }, 3000);
  };

  useEffect(() => {
    mounted.current = true;
    (async () => {
      setLoading(true);
      try {
        const p = await apiService.mktGetPerson(personId);
        if (!mounted.current) return;
        setPerson(p);
        setError(null);
      } catch (e: any) {
        if (mounted.current) setError(e?.message || 'Failed to load this profile.');
      } finally {
        if (mounted.current) setLoading(false);
      }
      // Reviews (best-effort)
      try {
        const r = await apiService.mktGetPersonReviews(personId);
        if (mounted.current) { setReviews(r.reviews); setReviewAvg(r.avg); setReviewCount(r.count); }
      } catch { /* non-fatal */ }
      // Their active service listings — filtered server-side by seller.
      try {
        const res = await apiService.mktGetListings({ kind: 'service', seller: personId });
        if (mounted.current) setListings(res.listings);
      } catch { /* non-fatal */ }
      // Identity + block state (best-effort).
      try {
        const me = await apiService.mktGetMe();
        if (mounted.current) setIsSelf(me?.id === personId);
      } catch { /* non-fatal */ }
      try {
        const blocks = await apiService.mktGetBlocks();
        if (mounted.current) setBlocked(blocks.includes(personId));
      } catch { /* non-fatal */ }
    })();
    return () => { mounted.current = false; };
  }, [personId]);

  const toggleBlock = async () => {
    if (working) return;
    setWorking(true);
    try {
      if (blocked) {
        await apiService.mktUnblockPerson(personId);
        if (!mounted.current) return;
        setBlocked(false);
        showToast('Unblocked. Their listings are visible again.');
      } else {
        await apiService.mktBlockPerson(personId);
        if (!mounted.current) return;
        setBlocked(true);
        showToast('Blocked. You won’t see their listings anymore.');
      }
      setConfirmOpen(false);
    } catch (e: any) {
      if (mounted.current) { setConfirmOpen(false); showToast(e?.message || 'Something went wrong. Please try again.'); }
    } finally {
      if (mounted.current) setWorking(false);
    }
  };

  return (
    <>
      <DashboardHeader />
      <div className={styles.container}>
        <div className={styles.topBar}>
          <button type="button" className={styles.backButton} onClick={() => navigate(-1)}>← Back</button>
        </div>

        {loading && <p className={styles.muted}><span className={styles.spinner} /> Loading…</p>}
        {error && !loading && <p className={styles.error}>{error}</p>}

        {!loading && person && (
          <>
            <div className={styles.panel} style={{ marginTop: 0 }}>
              <div className={styles.personCard} style={{ cursor: 'default' }}>
                <img className={`${styles.avatar} ${styles.avatarLg}`} src={person.profilePic || DEFAULT_AVATAR} alt={person.displayName}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR; }} />
                <div>
                  <div className={styles.personName} style={{ fontSize: 18 }}>{person.displayName}</div>
                  {person.tagline && <div className={styles.personSub}>{person.tagline}</div>}
                  <div className={styles.personSub}>{person.city || ''}</div>
                  <Stars value={reviewAvg || person.rating || 0} count={reviewCount} />
                  {person.memberSince && (
                    <div className={styles.personSub}>Member since {new Date(person.memberSince).getFullYear()}</div>
                  )}
                </div>
              </div>
              {!isSelf && (
                <div className={styles.statusControls}>
                  {blocked ? (
                    <button type="button" className={styles.secondaryBtn} onClick={() => setConfirmOpen(true)} disabled={working}>
                      Unblock
                    </button>
                  ) : (
                    <button type="button" className={styles.dangerBtn} onClick={() => setConfirmOpen(true)} disabled={working}>
                      🚫 Block
                    </button>
                  )}
                </div>
              )}
            </div>

            <h2 className={styles.sectionTitle}>Active listings</h2>
            {listings.length === 0 ? (
              <p className={styles.muted}>No active listings to show.</p>
            ) : (
              <div className={styles.grid}>
                {listings.map((l) => (
                  <ListingCard key={l.id} listing={l} onClick={() => navigate(`/customer/market/listing/${l.id}`)} />
                ))}
              </div>
            )}

            <h2 className={styles.sectionTitle}>Reviews <Stars value={reviewAvg} count={reviewCount} /></h2>
            {reviews.length === 0 ? (
              <p className={styles.muted}>No reviews yet.</p>
            ) : (
              <div className={styles.panel} style={{ marginTop: 8 }}>
                {reviews.map((r) => (
                  <div key={r.id} className={styles.reviewItem}>
                    <div className={styles.reviewHead}>
                      <img className={styles.avatar} style={{ width: 32, height: 32 }}
                        src={r.reviewer.profilePic || DEFAULT_AVATAR} alt={r.reviewer.displayName}
                        onError={(e) => { (e.currentTarget as HTMLImageElement).src = DEFAULT_AVATAR; }} />
                      <span className={styles.personName} style={{ fontSize: 14 }}>{r.reviewer.displayName}</span>
                      <span className={styles.stars}>{'★'.repeat(r.rating)}</span>
                    </div>
                    {r.comment && <p className={styles.reviewComment}>{r.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Block / Unblock confirm */}
      {confirmOpen && person && (
        <div className={styles.sheetOverlay} onClick={() => !working && setConfirmOpen(false)}>
          <div className={styles.sheet} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3 className={styles.sheetTitle}>
              {blocked ? `Unblock ${person.displayName}?` : `Block ${person.displayName}?`}
            </h3>
            <p className={styles.sheetSub}>
              {blocked
                ? 'You’ll see their listings and can message them again.'
                : 'You won’t see their listings or receive messages from them. You can unblock them later.'}
            </p>
            <div className={styles.sheetActions}>
              <button type="button" className={styles.secondaryBtn} onClick={() => setConfirmOpen(false)} disabled={working}>Cancel</button>
              <button type="button" className={blocked ? styles.primaryBtn : styles.dangerBtn} onClick={toggleBlock} disabled={working}>
                {working ? 'Working…' : blocked ? 'Unblock' : 'Block'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={styles.toast}>{toast}</div>}

      <Footer />
    </>
  );
}
